import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  DollarSign, CheckCircle, Clock, User, Trophy, 
  CreditCard, Send, Package, Key, MapPin, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PayoutsTab = ({ token, onRefresh }) => {
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [paymentNotes, setPaymentNotes] = useState({});

  const fetchPendingPayouts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/winners/pending-payouts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingPayouts(res.data || []);
    } catch (error) {
      console.error('Failed to fetch pending payouts:', error);
      toast.error('Failed to load pending payouts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchPendingPayouts();
    }
  }, [token]);

  const handleMarkPaid = async (winnerId) => {
    setProcessingId(winnerId);
    try {
      const note = paymentNotes[winnerId] || '';
      await axios.post(`${API}/admin/winners/${winnerId}/mark-paid?payment_note=${encodeURIComponent(note)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Prize marked as paid! User has been notified.');
      fetchPendingPayouts();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setProcessingId(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getPrizeIcon = (prizeType) => {
    switch (prizeType) {
      case 'money': return <DollarSign className="w-4 h-4 text-green-400" />;
      case 'game_code': return <Key className="w-4 h-4 text-blue-400" />;
      case 'item': return <Package className="w-4 h-4 text-purple-400" />;
      default: return <Trophy className="w-4 h-4 text-yellow-400" />;
    }
  };

  const totalPending = pendingPayouts
    .filter(p => p.prize_type === 'money')
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
          </div>
          <div>
            <h2 className="text-base sm:text-xl font-bold text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              PENDING PAYOUTS
            </h2>
            <p className="text-xs sm:text-sm text-gray-500">
              {pendingPayouts.length} prize{pendingPayouts.length !== 1 ? 's' : ''} awaiting payout
              {totalPending > 0 && <span className="text-green-400 ml-2">(${totalPending.toFixed(2)} total cash)</span>}
            </p>
          </div>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchPendingPayouts}
          disabled={loading}
          size="sm"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-green-400" />
            <div>
              <p className="text-2xl font-bold text-green-400">${totalPending.toFixed(2)}</p>
              <p className="text-xs text-gray-400">Cash to Pay</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <Key className="w-8 h-8 text-blue-400" />
            <div>
              <p className="text-2xl font-bold text-blue-400">
                {pendingPayouts.filter(p => p.prize_type === 'game_code').length}
              </p>
              <p className="text-xs text-gray-400">Game Codes</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-500/10 border-purple-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <Package className="w-8 h-8 text-purple-400" />
            <div>
              <p className="text-2xl font-bold text-purple-400">
                {pendingPayouts.filter(p => p.prize_type === 'item').length}
              </p>
              <p className="text-xs text-gray-400">Items to Ship</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Payouts List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full"></div>
        </div>
      ) : pendingPayouts.length === 0 ? (
        <Card className="bg-gray-900/50 border-gray-700">
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-green-400 mb-2">All Caught Up!</h3>
            <p className="text-gray-500 text-sm">
              No pending payouts. All claimed prizes have been paid.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {pendingPayouts.map((payout) => (
            <Card key={payout.id} className="bg-gray-900/80 border-gray-700 overflow-hidden">
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* User Info */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <img 
                      src={payout.profile_image_url || '/default-avatar.png'} 
                      alt={payout.display_name}
                      className="w-12 h-12 rounded-full border-2 border-gray-700"
                    />
                    <div>
                      <p className="text-white font-semibold">{payout.display_name}</p>
                      <p className="text-xs text-gray-500">@{payout.twitch_username}</p>
                    </div>
                  </div>

                  {/* Prize Info */}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-lg">
                        {payout.place === 1 ? '🥇' : payout.place === 2 ? '🥈' : '🥉'}
                      </span>
                      <span className="flex items-center gap-1 px-2 py-1 rounded text-sm font-semibold" 
                        style={{
                          backgroundColor: payout.prize_type === 'money' ? 'rgba(34,197,94,0.2)' : 
                            payout.prize_type === 'game_code' ? 'rgba(59,130,246,0.2)' : 'rgba(168,85,247,0.2)',
                          color: payout.prize_type === 'money' ? '#22c55e' : 
                            payout.prize_type === 'game_code' ? '#3b82f6' : '#a855f7'
                        }}
                      >
                        {getPrizeIcon(payout.prize_type)}
                        {payout.prize_type === 'money' && `$${payout.amount} ${payout.prize_currency}`}
                        {payout.prize_type === 'game_code' && 'Game Code'}
                        {payout.prize_type === 'item' && 'Physical Item'}
                      </span>
                      <span className="text-xs text-gray-500">{payout.challenge_title}</span>
                    </div>

                    {/* Payment/Shipping Details */}
                    {payout.prize_type === 'money' && payout.payment_method && (
                      <div className="flex items-center gap-2 p-2 bg-green-500/10 border border-green-500/30 rounded">
                        <span className="text-xs text-green-400 font-semibold uppercase">{payout.payment_method}:</span>
                        <code 
                          className="text-sm text-white bg-black/30 px-2 py-0.5 rounded cursor-pointer hover:bg-black/50"
                          onClick={() => copyToClipboard(payout.payment_info)}
                          title="Click to copy"
                        >
                          {payout.payment_info}
                        </code>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-6 px-2 text-green-400"
                          onClick={() => copyToClipboard(payout.payment_info)}
                        >
                          Copy
                        </Button>
                      </div>
                    )}

                    {payout.prize_type === 'item' && payout.shipping_address && (
                      <div className="p-2 bg-purple-500/10 border border-purple-500/30 rounded">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                          <pre className="text-xs text-gray-300 whitespace-pre-wrap font-sans">
                            {payout.shipping_address}
                          </pre>
                        </div>
                      </div>
                    )}

                    {/* Claimed time */}
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      Claimed {new Date(payout.claimed_at).toLocaleString()}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 flex-shrink-0 lg:w-48">
                    <Input
                      placeholder="Payment note (optional)"
                      value={paymentNotes[payout.id] || ''}
                      onChange={(e) => setPaymentNotes({...paymentNotes, [payout.id]: e.target.value})}
                      className="h-8 text-xs bg-gray-800 border-gray-700"
                    />
                    <Button
                      onClick={() => handleMarkPaid(payout.id)}
                      disabled={processingId === payout.id}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {processingId === payout.id ? (
                        'Processing...'
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Mark as Paid
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PayoutsTab;
